import { useEffect, useState, type SyntheticEvent } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router';
import { useAuth } from '../auth/AuthContext';
import { CameraScanner } from '../components/CameraScanner';
import { Alert, Button, TextField } from '../components/ui';
import { checkPairing, registerPageTarget } from '../native';
import { readDirectStart } from '../pos/directStart';
import { lastPosEvents } from '../pos/sessions';
import { HomePage } from './home';
import { PairingPage } from './pairing';

/**
 * Kasse per QR koppeln (Plan 06): Die Kamera liest den Kopplungs-QR des Basars und führt zur Kopplungsseite
 * (`/kasse-koppeln#token=…`). Die Seite braucht kein Konto, denn die Kopplung per Name und PIN läuft ohne Anmeldung.
 * Die Kamera meldet hier jeden Text (`kind="any"`), denn der Kopplungs-QR ist ein Link und kein Etikettencode.
 * Der QR der Kassenseite (`/basare/<id>/kasse`) führt zur Kasse mit Anmeldung. Ohne Kamera kann der Link eingefügt werden. Das Token bleibt im Fragment und geht nie in Pfad oder Query.
 */
export function PairScanPage() {
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [link, setLink] = useState('');

  function accept(text: string): void {
    const result = checkPairing(text);
    if (!result.ok) {
      // QR der Kassenseite (Kassen-Übersicht): öffnet die Kasse mit Anmeldung statt der Kopplung ohne Konto
      const register = registerPageTarget(text);
      if (register !== null) {
        void navigate(register, { replace: true });
        return;
      }
      setError(
        result.reason === 'host'
          ? 'Dieser QR-Code gehört nicht zu BARSO (fremde Adresse). Bitte den QR-Code Ihres Basars scannen.'
          : 'Das ist kein Kassen-Code von BARSO. Bitte den QR-Code des Basars scannen.',
      );
      return;
    }
    void navigate(result.target, { replace: true });
  }

  function submit(event: SyntheticEvent) {
    event.preventDefault();
    accept(link);
  }

  return (
    <div className="stack stack--lg pair-scan">
      <h1>Kasse per QR koppeln</h1>
      <p>
        Halten Sie die Kamera auf den QR-Code Ihrer Kasse (Aushang oder Bildschirm des
        Veranstalters).
      </p>
      {error !== null && <Alert kind="error">{error}</Alert>}
      <CameraScanner kind="any" variant="tall" onCode={accept} onClose={() => void navigate('/')} />
      <form className="stack" onSubmit={submit} noValidate>
        <TextField
          label="Oder Link einfügen"
          hint="Der Link beginnt mit https:// und endet auf #token=…"
          value={link}
          autoCapitalize="none"
          autoCorrect="off"
          spellCheck={false}
          onChange={(change) => {
            setLink(change.target.value);
          }}
        />
        <Button icon="link" type="submit" variant="secondary" disabled={link.trim() === ''}>
          Link übernehmen
        </Button>
      </form>
    </div>
  );
}

/**
 * Kopplungsseite mit frischem Zustand je Aufruf: Ein zweiter Kopplungs-Link bei schon geöffneter Seite (neuer
 * `location.key`) lädt das neue Token, statt das erste weiter zu benutzen.
 */
export function KeyedPairingPage() {
  const { key } = useLocation();
  return <PairingPage key={key} />;
}

/** Pro App-Start nur einmal direkt in die Kasse springen, danach bleibt die Startseite erreichbar („Entkoppeln“). */
let openedAtStart = false;

/** Nur für Tests: setzt die Merkung „beim Start schon geöffnet“ zurück. */
export function resetNativeStartForTests(): void {
  openedAtStart = false;
}

/**
 * Startseite der nativen App („/“). Ein gekoppeltes Gerät öffnet beim Start direkt die Kasse. Ohne Kopplung steht
 * „Kasse per QR koppeln“ vorn (ohne Anmeldung nutzbar). Angemeldete ohne gekoppelte Kasse sehen wie sonst ihre
 * Startseite (oder, mit „Beim Öffnen direkt zur Kasse“, die Kasse bzw. ihre Kassen); Angemeldete mit gekoppeltem Gerät kommen über „Zur Übersicht“ (`/?ansicht=uebersicht`) dorthin.
 */
export function NativeStart() {
  const { status } = useAuth();
  const navigate = useNavigate();
  // Gekoppelt = die zuletzt benutzte Kasse läuft mit Gerätesitzung (Schnellzugang, Plan 06)
  const last = lastPosEvents()[0];
  const paired = last?.device === true ? last.eventId : null;
  const [params] = useSearchParams();

  useEffect(() => {
    if (paired === null || openedAtStart) return;
    openedAtStart = true;
    void navigate(`/kasse-geraet/${paired}`, { replace: true });
  }, [paired, navigate]);

  // „Beim Öffnen direkt zur Kasse“ (Einstellung im Konto-Menü): angemeldet ohne Kopplung zur Kasse bzw. zur Liste „Ihre Kassen“
  const direct = status === 'authenticated' && paired === null && params.get('ansicht') === null;
  useEffect(() => {
    if (!direct || openedAtStart || !readDirectStart()) return;
    openedAtStart = true;
    void navigate('/kasse', { replace: true });
  }, [direct, navigate]);

  if (status === 'authenticated' && (paired === null || params.get('ansicht') === 'uebersicht')) {
    return <HomePage />;
  }

  return (
    <div className="stack stack--lg native-start">
      <h1>BARSO Kasse</h1>
      {paired === null ? (
        <>
          <p>
            Mit dem QR-Code Ihres Basars wird dieses Gerät zur Kasse. Ein Konto brauchen Sie dafür
            nicht immer.
          </p>
          <Link className="btn native-start__main" to="/kasse-scannen">
            Kasse per QR koppeln
          </Link>
          <Link className="btn btn--secondary" to="/anmelden">
            Mit Konto anmelden
          </Link>
        </>
      ) : (
        <>
          <p>Dieses Gerät ist als Kasse gekoppelt.</p>
          <Link className="btn native-start__main" to={`/kasse-geraet/${paired}`}>
            Kasse öffnen
          </Link>
          <Link className="btn btn--secondary" to="/kasse-scannen">
            Andere Kasse koppeln
          </Link>
          {status === 'authenticated' && (
            <Link className="btn btn--secondary" to="/?ansicht=uebersicht">
              Zur Übersicht
            </Link>
          )}
        </>
      )}
    </div>
  );
}
